import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../utils/api';
import { v4 as uuidv4 } from 'uuid';
import { useUsers } from './UsersContext';
import { DEFAULT_ACCOUNT_MANAGER } from '../utils/defaultAccountManager';

const ClientsContext = createContext();

// Pipeline stages definition
export const PIPELINE_STAGES = {
  lead: { label: 'Lead', icon: 'radio-button-off-outline', color: '#8896a6' },
  prospect: { label: 'Prospect', icon: 'flash-outline', color: '#0066cc' },
  proposal: { label: 'Proposal', icon: 'document-text-outline', color: '#7c3aed' },
  negotiation: { label: 'Negotiation', icon: 'people-outline', color: '#d97706' },
  closed_won: { label: 'Closed Won', icon: 'trophy-outline', color: '#059669' },
  active: { label: 'Active', icon: 'checkmark-circle-outline', color: '#059669' },
  renewal: { label: 'Renewal', icon: 'refresh-outline', color: '#2563eb' },
  lost: { label: 'Lost', icon: 'close-circle-outline', color: '#dc2626' },
  on_hold: { label: 'On Hold', icon: 'pause-circle-outline', color: '#8896a6' },
};

// Default client structure helpers
const getDefaultOverrides = (config) => ({
  waitingPeriod: config?.employerPlanRules?.waiting_period_days || 60,
  minWeeklyHours: config?.employerPlanRules?.minimum_weekly_hours || 20,
  eligibleClasses: {
    fulltime_w2: config?.employerPlanRules?.eligible_classes?.full_time_w2 !== false,
    parttime_w2: config?.employerPlanRules?.eligible_classes?.part_time_w2 !== false,
    seasonal_temp: config?.employerPlanRules?.eligible_classes?.seasonal_temp || false,
    union_cba: config?.employerPlanRules?.eligible_classes?.union_cba || false,
  },
  localMinWage: config?.employerPlanRules?.state_local_min_wage_override || 7.25,
  availablePlanTiers: config?.planTiers?.map(t => t.label) || ['1500', '1200', '1050', '900', '750', '600'],
});

const getDefaultAnalysisControls = (config) => ({
  payFrequency: config?.engineSettings?.payPeriodsPerYear || 12,
  optimizationStrategy: config?.engineSettings?.optimizationStrategy || 'employer',
  minEmployeeNet: config?.engineSettings?.minEmployeeNetBenefit || 0,
  includeFuta: config?.engineSettings?.includeFutaSavings || false,
});

// Create empty client
export function createEmptyClient(config, currentUser = null) {
  // Default Account Manager assignment:
  //  - If creator is an Account Manager, auto-assign to themselves.
  //  - Otherwise default to Ryan Young (system default AM).
  let defaultAssignedTo = DEFAULT_ACCOUNT_MANAGER.id;
  if (currentUser?.role === 'account_manager') {
    defaultAssignedTo = currentUser.id || currentUser._id || defaultAssignedTo;
  }

  return {
    company: {
      legalName: '',
      dba: '',
      ein: '',
      industry: '',
      address: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      payrollProvider: '',
      paySchedule: '',
      planYearStart: 'January',
      ach: {
        bankName: '',
        routing: '',
        account: '',
        accountType: 'checking',
      },
    },
    crm: {
      stage: 'lead',
      source: '',
      assignedTo: defaultAssignedTo,
      priority: 'medium',
      confidence: 50,
      estimatedValue: 0,
      followUp: {
        action: '',
        dueDate: null,
        reminder: 'none',
        assignedTo: '',
      },
    },
  };
}

// Log activity helper
function addActivityToClient(client, type, description) {
  const activity = {
    id: uuidv4(),
    date: new Date().toISOString(),
    type,
    description,
  };
  
  return {
    ...client,
    activities: [activity, ...(client.activities || [])],
  };
}

export function ClientsProvider({ children, config }) {
  const { currentUser } = useUsers();  // Get current user from UsersContext
  const [currentClient, setCurrentClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  // Load clients from backend ONLY when user is authenticated
  useEffect(() => {
    const loadClients = async () => {
      // Only load if user is authenticated
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        const clientsData = await api.clients.list();
        setClients(clientsData);
      } catch (error) {
        console.error('Failed to load clients:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadClients();
  }, [currentUser]);  // Re-load when currentUser changes
  
  // Create new client
  const createNewClient = useCallback(() => {
    const newClient = createEmptyClient(config, currentUser);
    setCurrentClient(newClient);
    setHasUnsavedChanges(false);
  }, [config, currentUser]);
  
  // Clear unsaved
  const clearUnsaved = useCallback(() => {
    setCurrentClient(null);
    setHasUnsavedChanges(false);
  }, []);
  
  // Update current client (local state only)
  const updateCurrentClient = useCallback((updates) => {
    setCurrentClient(prev => {
      if (!prev) return null;
      return { ...prev, ...updates };
    });
    if (currentClient?.id) {
      setHasUnsavedChanges(true);
    }
  }, [currentClient]);
  
  // Update company info
  const updateCompanyInfo = useCallback(async (companyUpdates) => {
    if (!currentClient) return;
    
    const updated = {
      ...currentClient,
      company: { ...currentClient.company, ...companyUpdates },
    };
    
    setCurrentClient(updated);
    setHasUnsavedChanges(true);
    
    // If it's a saved client, update backend
    if (currentClient.id) {
      try {
        const savedClient = await api.clients.update(currentClient.id, {
          company: updated.company,
        });
        setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
        setCurrentClient(savedClient);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to update company info:', error);
      }
    }
  }, [currentClient]);
  
  // Update CRM info
  const updateCRM = useCallback(async (crmUpdates) => {
    if (!currentClient) return;
    
    const oldStage = currentClient.crm.stage;
    const newStage = crmUpdates.stage;
    
    let updated = {
      ...currentClient,
      crm: { ...currentClient.crm, ...crmUpdates },
    };
    
    // Log stage change
    if (oldStage && newStage && oldStage !== newStage) {
      const stageLabel = stage => PIPELINE_STAGES[stage]?.label || stage;
      updated = addActivityToClient(updated, 'auto', `Stage changed: ${stageLabel(oldStage)} → ${stageLabel(newStage)}`);
    }
    
    setCurrentClient(updated);
    setHasUnsavedChanges(true);
    
    // If it's a saved client, update backend
    if (currentClient.id) {
      try {
        const savedClient = await api.clients.update(currentClient.id, updated);
        setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
        setCurrentClient(savedClient);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to update CRM:', error);
      }
    }
  }, [currentClient]);
  
  // Add/Update contact
  const saveContact = useCallback(async (clientId, contact) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    
    const isNew = !contact.id;
    const contactWithId = { ...contact, id: contact.id || uuidv4() };
    
    let updatedContacts;
    if (isNew) {
      if (contactWithId.isPrimary) {
        updatedContacts = [...client.contacts.map(c => ({ ...c, isPrimary: false })), contactWithId];
      } else {
        updatedContacts = [...client.contacts, contactWithId];
      }
    } else {
      if (contactWithId.isPrimary) {
        updatedContacts = client.contacts.map(c => 
          c.id === contactWithId.id ? contactWithId : { ...c, isPrimary: false }
        );
      } else {
        updatedContacts = client.contacts.map(c => c.id === contactWithId.id ? contactWithId : c);
      }
    }
    
    let updated = {
      ...client,
      contacts: updatedContacts,
    };
    
    if (isNew) {
      updated = addActivityToClient(updated, 'auto', `Contact added: ${contactWithId.firstName} ${contactWithId.lastName}`);
    }
    
    setCurrentClient(updated);
    setHasUnsavedChanges(true);
    
    // If it's a saved client, update backend
    if (client.id) {
      try {
        const savedClient = await api.clients.update(client.id, updated);
        setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
        setCurrentClient(savedClient);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to save contact:', error);
      }
    }
  }, [clients]);
  
  // Delete contact
  const deleteContact = useCallback(async (clientId, contactId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    
    const updated = {
      ...client,
      contacts: client.contacts.filter(c => c.id !== contactId),
    };
    
    setCurrentClient(updated);
    setHasUnsavedChanges(true);
    
    // If it's a saved client, update backend
    if (client.id) {
      try {
        const savedClient = await api.clients.update(client.id, updated);
        setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
        setCurrentClient(savedClient);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to delete contact:', error);
      }
    }
  }, [clients]);
  
  // Add manual note
  const addNote = useCallback(async (note) => {
    if (!currentClient) return;
    
    const updated = addActivityToClient(currentClient, 'note', note);
    setCurrentClient(updated);
    setHasUnsavedChanges(true);
    
    // If it's a saved client, update backend
    if (currentClient.id) {
      try {
        const savedClient = await api.clients.update(currentClient.id, updated);
        setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
        setCurrentClient(savedClient);
        setHasUnsavedChanges(false);
      } catch (error) {
        console.error('Failed to add note:', error);
      }
    }
  }, [currentClient]);
  
  // Update overrides (legacy - for backward compatibility)
  const updateOverrides = useCallback((overrideUpdates) => {
    setCurrentClient(prev => {
      if (!prev) return null;
      return {
        ...prev,
        overrides: { ...prev.overrides, ...overrideUpdates },
      };
    });
    setHasUnsavedChanges(true);
  }, []);
  
  // Update analysis controls (legacy - for backward compatibility)
  const updateAnalysisControls = useCallback((controlUpdates) => {
    setCurrentClient(prev => {
      if (!prev) return null;
      return {
        ...prev,
        analysisControls: { ...prev.analysisControls, ...controlUpdates },
      };
    });
    setHasUnsavedChanges(true);
  }, []);
  
  // Save client (create or update)
  const saveClient = useCallback(async (clientData) => {
    const isNew = !clientData.id;
    
    try {
      let savedClient;
      
      if (isNew) {
        // Add creation activity
        const clientWithActivity = addActivityToClient(clientData, 'auto', 'Client created');
        savedClient = await api.clients.create(clientWithActivity);
        setClients(prev => [savedClient, ...prev]);
      } else {
        savedClient = await api.clients.update(clientData.id, clientData);
        setClients(prev => prev.map(c => c.id === savedClient.id ? savedClient : c));
      }
      
      setCurrentClient(savedClient);
      setHasUnsavedChanges(false);
      
      return savedClient;
    } catch (error) {
      console.error('Failed to save client:', error);
      throw error;
    }
  }, []);
  
  // Load client
  const loadClient = useCallback(async (clientId) => {
    try {
      const client = await api.clients.get(clientId);
      setCurrentClient(client);
      setHasUnsavedChanges(false);
      return client;
    } catch (error) {
      // Fallback to local cache
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setCurrentClient(client);
        setHasUnsavedChanges(false);
        return client;
      }
      console.error('Failed to load client:', error);
      return null;
    }
  }, [clients]);
  
  // Delete client
  const deleteClient = useCallback(async (clientId) => {
    try {
      await api.clients.delete(clientId);
      setClients(prev => prev.filter(c => c.id !== clientId));
      if (currentClient?.id === clientId) {
        setCurrentClient(null);
      }
    } catch (error) {
      console.error('Failed to delete client:', error);
      throw error;
    }
  }, [currentClient]);
  
  // Update analysis configuration
  const updateAnalysisConfig = useCallback(async (clientId, configUpdates, userId) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) return null;
      
      // Check if locked
      if (client.analysisConfig?.isLocked) {
        console.warn('Cannot update locked analysis configuration');
        return null;
      }
      
      const updatedConfig = {
        ...client.analysisConfig,
        ...configUpdates,
        lastModifiedAt: new Date().toISOString(),
        lastModifiedBy: userId,
      };
      
      const updated = {
        ...client,
        analysisConfig: updatedConfig,
      };
      
      // Log activity
      const loggedClient = addActivityToClient(updated, 'auto', 'Analysis settings updated');
      
      const savedClient = await api.clients.update(clientId, loggedClient);
      setClients(prev => prev.map(c => c.id === clientId ? savedClient : c));
      
      if (currentClient?.id === clientId) {
        setCurrentClient(savedClient);
      }
      
      return savedClient;
    } catch (error) {
      console.error('Failed to update analysis config:', error);
      return null;
    }
  }, [clients, currentClient]);
  
  // Phase 17.1 — Silent backend sync of just the summary rollup. Used by
  // BulkView's auto-sync useEffect to repair stale `client.summary` records
  // (e.g. clients whose census was uploaded before the summary-rollup fix).
  // Updates the backend, the `clients` array (so the dropdown row label and
  // Clients-table cells refresh), and `currentClient` if applicable. Does
  // NOT toggle `hasUnsavedChanges` — this is an automatic data-integrity
  // repair, not a user action.
  const syncClientSummary = useCallback(async (clientId, summary) => {
    if (!clientId || !summary) return null;
    try {
      const target = clients.find((c) => c.id === clientId) || (currentClient?.id === clientId ? currentClient : null);
      if (!target) return null;
      const merged = { ...target, summary };
      const savedClient = await api.clients.update(clientId, merged);
      setClients((prev) => prev.map((c) => (c.id === clientId ? savedClient : c)));
      if (currentClient?.id === clientId) setCurrentClient(savedClient);
      return savedClient;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('[ClientsContext] syncClientSummary failed:', error);
      return null;
    }
  }, [clients, currentClient]);

  // Lock analysis configuration
  const lockAnalysisConfig = useCallback(async (clientId, userId, reason = '') => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) return null;
      
      const updated = {
        ...client,
        analysisConfig: {
          ...client.analysisConfig,
          isLocked: true,
          lockedAt: new Date().toISOString(),
          lockedBy: userId,
          lockedReason: reason,
        },
      };
      
      const loggedClient = addActivityToClient(updated, 'auto', `Analysis settings locked${reason ? `: ${reason}` : ''}`);
      
      const savedClient = await api.clients.update(clientId, loggedClient);
      setClients(prev => prev.map(c => c.id === clientId ? savedClient : c));
      
      if (currentClient?.id === clientId) {
        setCurrentClient(savedClient);
      }
      
      return savedClient;
    } catch (error) {
      console.error('Failed to lock analysis config:', error);
      return null;
    }
  }, [clients, currentClient]);
  
  // Unlock analysis configuration
  const unlockAnalysisConfig = useCallback(async (clientId, userId, reason = '') => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) return null;
      
      const updated = {
        ...client,
        analysisConfig: {
          ...client.analysisConfig,
          isLocked: false,
          lockedAt: null,
          lockedBy: null,
          lockedReason: '',
        },
      };
      
      const loggedClient = addActivityToClient(updated, 'auto', `Analysis settings unlocked${reason ? `. Reason: ${reason}` : ''}`);
      
      const savedClient = await api.clients.update(clientId, loggedClient);
      setClients(prev => prev.map(c => c.id === clientId ? savedClient : c));
      
      if (currentClient?.id === clientId) {
        setCurrentClient(savedClient);
      }
      
      return savedClient;
    } catch (error) {
      console.error('Failed to unlock analysis config:', error);
      return null;
    }
  }, [clients, currentClient]);
  
  // Add document to client
  const addDocument = useCallback(async (clientId, document) => {
    try {
      const client = clients.find(c => c.id === clientId);
      if (!client) return null;
      
      const updated = {
        ...client,
        documents: [...(client.documents || []), document],
      };
      
      const savedClient = await api.clients.update(clientId, updated);
      setClients(prev => prev.map(c => c.id === clientId ? savedClient : c));
      
      if (currentClient?.id === clientId) {
        setCurrentClient(savedClient);
      }
      
      return savedClient;
    } catch (error) {
      console.error('Failed to add document:', error);
      return null;
    }
  }, [clients, currentClient]);
  
  // Portal access management (placeholder - will be implemented in Phase 3)
  const enablePortalAccess = useCallback(async (clientId, contactId, userId) => {
    try {
      const result = await api.clients.enablePortalAccess(clientId, contactId);
      
      // Refresh clients list to get updated data
      const updatedClients = await api.clients.list();
      setClients(updatedClients);
      
      return result;
    } catch (error) {
      console.error('Failed to enable portal access:', error);
      return { success: false, error: error.message || 'Failed to enable portal access' };
    }
  }, []);
  
  const disablePortalAccess = useCallback(async (clientId, contactId, userId) => {
    try {
      const result = await api.clients.disablePortalAccess(clientId, contactId);
      
      // Refresh clients list
      const updatedClients = await api.clients.list();
      setClients(updatedClients);
      
      return result;
    } catch (error) {
      console.error('Failed to disable portal access:', error);
      return { success: false, error: error.message || 'Failed to disable portal access' };
    }
  }, []);
  
  const resetContactPassword = useCallback(async (clientId, contactId, userId) => {
    try {
      const result = await api.clients.resetContactPassword(clientId, contactId);
      
      // Refresh clients list
      const updatedClients = await api.clients.list();
      setClients(updatedClients);
      
      return result;
    } catch (error) {
      console.error('Failed to reset password:', error);
      return { success: false, error: error.message || 'Failed to reset password' };
    }
  }, []);
  
  const checkEmailUniqueness = useCallback((email) => {
    // This will be implemented with backend in Phase 3
    return false;
  }, []);
  
  const getAllPortalContacts = useCallback(() => {
    const contacts = [];
    
    clients.forEach(client => {
      (client.contacts || []).forEach(contact => {
        if (contact.portalAccess) {
          contacts.push({
            id: contact.id,
            email: contact.email,
            firstName: contact.firstName,
            lastName: contact.lastName,
            role: contact.role || 'Contact',
            company: client.company?.legalName || client.company?.dba || 'Unknown Company',
            companyId: client.id,
            status: contact.portalStatus || 'active',
            lastLoginAt: contact.lastLoginAt,
            loginCount: contact.loginCount || 0,
            portalEnabledAt: contact.portalEnabledAt,
            isPrimary: contact.isPrimary || false,
          });
        }
      });
    });
    
    return contacts;
  }, [clients]);
  
  // Get pipeline summary
  const getPipelineSummary = useCallback(() => {
    const summary = {
      totalClients: clients.length,
      byStage: {},
      totalEmployees: 0,
      eligibleEmployees: 0,
      annualFicaSavings: 0,
      annualHcRevenue: 0,
      followUpsDue: {
        overdue: [],
        today: [],
        thisWeek: [],
      },
    };
    
    Object.keys(PIPELINE_STAGES).forEach(stage => {
      summary.byStage[stage] = 0;
    });
    
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekFromNow = new Date(today);
    weekFromNow.setDate(weekFromNow.getDate() + 7);
    
    clients.forEach(client => {
      if (client.crm.stage) {
        summary.byStage[client.crm.stage] = (summary.byStage[client.crm.stage] || 0) + 1;
      }
      
      if (client.summary) {
        summary.totalEmployees += client.summary.totalEmployees || 0;
        summary.eligibleEmployees += client.summary.eligible || 0;
        summary.annualFicaSavings += client.summary.annualEmployerFica || 0;
        summary.annualHcRevenue += client.summary.annualHcRevenue || 0;
      }
      
      if (client.crm.followUp?.action && client.crm.followUp.dueDate) {
        const dueDate = new Date(client.crm.followUp.dueDate);
        const followUp = {
          clientId: client.id,
          clientName: client.company.legalName || 'Unnamed Client',
          action: client.crm.followUp.action,
          dueDate: client.crm.followUp.dueDate,
        };
        
        if (dueDate < today) {
          summary.followUpsDue.overdue.push(followUp);
        } else if (dueDate.getTime() === today.getTime()) {
          summary.followUpsDue.today.push(followUp);
        } else if (dueDate < weekFromNow) {
          summary.followUpsDue.thisWeek.push(followUp);
        }
      }
    });
    
    return summary;
  }, [clients]);
  
  const value = {
    currentClient,
    activeClient: currentClient, // Alias for BulkView compatibility
    clients,
    loading,
    hasUnsavedChanges,
    createNewClient,
    clearUnsaved,
    updateCurrentClient,
    updateCompanyInfo,
    updateCRM,
    saveContact,
    deleteContact,
    addNote,
    updateOverrides,
    updateAnalysisControls,
    saveClient,
    loadClient,
    deleteClient,
    updateAnalysisConfig,
    lockAnalysisConfig,
    unlockAnalysisConfig,
    syncClientSummary,
    addDocument,
    enablePortalAccess,
    disablePortalAccess,
    resetContactPassword,
    checkEmailUniqueness,
    getAllPortalContacts,
    getPipelineSummary,
    setHasUnsavedChanges,
  };
  
  return (
    <ClientsContext.Provider value={value}>
      {children}
    </ClientsContext.Provider>
  );
}

export function useClients() {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error('useClients must be used within ClientsProvider');
  }
  return context;
}
